import { useCallback, useEffect, useState } from 'react';
import {
  addLaborProfileRate,
  createLaborProfile,
  getLaborProfiles,
  setLaborProfileActive,
} from './laborProfile.api';

const today = () => new Date().toISOString().slice(0, 10);
const emptyProfile = { code: '', label: '', costHourlyRate: '', suggestedSellingHourlyRate: '', validFrom: today() };
const money = (value) => value === null || value === undefined
  ? '-'
  : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(value));
const date = (value) => value ? new Intl.DateTimeFormat('fr-FR').format(new Date(`${value}T00:00:00`)) : 'aujourd’hui';

export default function LaborProfileSettingsPage() {
  const [profiles, setProfiles] = useState([]);
  const [form, setForm] = useState(emptyProfile);
  const [rateForms, setRateForms] = useState({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setProfiles(await getLaborProfiles());
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitProfile = async (event) => {
    event.preventDefault();
    setSaving(true); setError(''); setMessage('');
    try {
      await createLaborProfile(form);
      setForm({ ...emptyProfile, validFrom: today() });
      setMessage('Profil métier créé.');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const submitRate = async (profileId) => {
    const values = rateForms[profileId] || {};
    setSaving(true); setError(''); setMessage('');
    try {
      await addLaborProfileRate(profileId, values);
      setRateForms((current) => ({ ...current, [profileId]: {} }));
      setMessage('Nouveau tarif enregistré sans modification de l’historique.');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="page-container labor-profile-settings">
      <header>
        <p className="page-kicker">Devis · Référentiel</p>
        <h1>Profils métiers et coûts horaires</h1>
        <p>Les tarifs sont datés. Une nouvelle valeur clôture automatiquement la précédente.</p>
      </header>

      {error && <div role="alert" className="form-alert form-alert--error">{error}</div>}
      {message && <div role="status" className="form-alert form-alert--success">{message}</div>}

      <section className="basic-card">
        <h2>Créer un profil</h2>
        <form onSubmit={submitProfile} className="labor-profile-form">
          <label>Code<input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="PROJETEUR" /></label>
          <label>Libellé<input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Projeteur" /></label>
          <label>Coût horaire<input required min="0" step="0.0001" type="number" value={form.costHourlyRate} onChange={(e) => setForm({ ...form, costHourlyRate: e.target.value })} /></label>
          <label>Vente suggérée<input min="0" step="0.0001" type="number" value={form.suggestedSellingHourlyRate} onChange={(e) => setForm({ ...form, suggestedSellingHourlyRate: e.target.value })} /></label>
          <label>Prise d’effet<input required type="date" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} /></label>
          <button className="basic-button" disabled={saving} type="submit">Créer le profil</button>
        </form>
      </section>

      <section className="labor-profile-list" aria-label="Profils métiers">
        {profiles.map((profile) => {
          const rateForm = rateForms[profile.id] || {};
          return (
            <article className="basic-card labor-profile-card" key={profile.id}>
              <header>
                <div><strong>{profile.code}</strong><h2>{profile.label}</h2></div>
                <button
                  type="button"
                  className="basic-button"
                  onClick={async () => { await setLaborProfileActive(profile.id, !profile.isActive); await load(); }}
                >
                  {profile.isActive ? 'Désactiver' : 'Réactiver'}
                </button>
              </header>
              <span className={`status-badge ${profile.isActive ? 'status-badge--success' : ''}`}>
                {profile.isActive ? 'Actif' : 'Inactif'}
              </span>

              <h3>Historique tarifaire</h3>
              <table>
                <thead><tr><th>Période</th><th>Coût horaire</th><th>Vente suggérée</th></tr></thead>
                <tbody>
                  {(profile.rates || []).map((rate) => (
                    <tr key={rate.id}>
                      <td>Du {date(rate.validFrom)} {rate.validTo ? `au ${date(rate.validTo)}` : 'sans date de fin'}</td>
                      <td>{money(rate.costHourlyRate)}</td>
                      <td>{money(rate.suggestedSellingHourlyRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="labor-profile-rate-form">
                <label>Nouveau coût<input min="0" step="0.0001" type="number" value={rateForm.costHourlyRate || ''} onChange={(e) => setRateForms({ ...rateForms, [profile.id]: { ...rateForm, costHourlyRate: e.target.value } })} /></label>
                <label>Vente suggérée<input min="0" step="0.0001" type="number" value={rateForm.suggestedSellingHourlyRate || ''} onChange={(e) => setRateForms({ ...rateForms, [profile.id]: { ...rateForm, suggestedSellingHourlyRate: e.target.value } })} /></label>
                <label>Prise d’effet<input type="date" value={rateForm.validFrom || ''} onChange={(e) => setRateForms({ ...rateForms, [profile.id]: { ...rateForm, validFrom: e.target.value } })} /></label>
                <button type="button" className="basic-button" disabled={saving || !rateForm.costHourlyRate || !rateForm.validFrom} onClick={() => submitRate(profile.id)}>Ajouter le tarif</button>
              </div>
            </article>
          );
        })}
      </section>
    </main>
  );
}
